import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { customBilling, UNIT_KIND_LABELS, SCOPE_LABELS } from '@/api/customBilling';
import type { BillableItemRow, BillableItemBody, BillableScope, UnitKind } from '@/api/customBilling';
import { PageHeader } from '@/components/PageHeader';
import { DataTable } from '@/components/DataTable';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogClose,
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Plus, MoreHorizontal, Trash2, Pencil } from 'lucide-react';

function formatMoney(cents: number): string {
  return (cents / 100).toLocaleString(undefined, { style: 'currency', currency: 'USD' });
}

export function BillableItemsPage() {
  const qc = useQueryClient();
  const [createOpen, setCreateOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<BillableItemRow | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<BillableItemRow | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['billableItems'],
    queryFn: () => customBilling.listItems({ limit: 500 }),
  });
  const rows = data?.rows ?? [];

  const remove = useMutation({
    mutationFn: (id: string) => customBilling.deleteItem(id),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['billableItems'] }); setDeleteTarget(null); },
  });

  const columns = [
    { header: 'Code', cell: (r: BillableItemRow) => <span className="font-mono text-xs text-slate-500">{r.code}</span> },
    { header: 'Name', cell: (r: BillableItemRow) => <span className="text-sm font-medium">{r.name}</span> },
    { header: 'Scope', cell: (r: BillableItemRow) => (
      <Badge variant="outline" className="text-[10px]">{SCOPE_LABELS[r.scope]}</Badge>
    ) },
    { header: 'Unit', cell: (r: BillableItemRow) => (
      <span className="text-xs text-slate-600">{UNIT_KIND_LABELS[r.unit_kind]}</span>
    ) },
    { header: 'Default price', cell: (r: BillableItemRow) => (
      <span className="font-mono text-sm tabular-nums">{formatMoney(r.default_unit_price_cents)}</span>
    ) },
    { header: 'Reseller enabled', cell: (r: BillableItemRow) => (
      r.available_to_reseller
        ? <Badge variant="default">Available</Badge>
        : <Badge variant="secondary">Owner-only</Badge>
    ) },
    { header: 'Active', cell: (r: BillableItemRow) => (
      r.active ? <Badge variant="default">Active</Badge> : <Badge variant="secondary">Inactive</Badge>
    ) },
    { header: '', className: 'w-10 text-end', cell: (r: BillableItemRow) => (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button type="button" aria-label="Row actions" onClick={(e) => e.stopPropagation()}
            className="rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700">
            <MoreHorizontal className="h-4 w-4" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
          <DropdownMenuItem onClick={() => setEditTarget(r)}>
            <Pencil className="h-3.5 w-3.5 mr-2" /> Edit
          </DropdownMenuItem>
          <DropdownMenuItem
            className="text-red-600 focus:text-red-600 focus:bg-red-50"
            onClick={() => setDeleteTarget(r)}>
            <Trash2 className="h-3.5 w-3.5 mr-2" /> Delete
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    ) },
  ];

  return (
    <>
      <PageHeader
        eyebrow="Platform billing"
        title="Billable items"
        description="Master list of what the platform can bill for. Resellers configure their markup per item via Pricing Rules; customers inherit the reseller's prices."
        actions={
          <Button size="sm" onClick={() => setCreateOpen(true)}>
            <Plus className="h-4 w-4 mr-1.5" /> New item
          </Button>
        }
      />

      {isLoading ? (
        <div className="text-sm text-slate-400">Loading…</div>
      ) : (
        <DataTable rows={rows} columns={columns} empty="No billable items defined yet." />
      )}

      {(createOpen || editTarget) && (
        <ItemDialog
          existing={editTarget ?? undefined}
          onClose={() => { setCreateOpen(false); setEditTarget(null); }}
        />
      )}
      {deleteTarget && (
        <Dialog open onOpenChange={(v) => { if (!v) setDeleteTarget(null); }}>
          <DialogContent className="max-w-sm">
            <DialogHeader><DialogTitle>Delete billable item?</DialogTitle></DialogHeader>
            <p className="text-sm text-slate-600">
              <span className="font-medium">{deleteTarget.name}</span> (<code>{deleteTarget.code}</code>) will
              be soft-deleted. Existing pricing rules referencing it remain but won&rsquo;t be invoiced.
            </p>
            <DialogFooter>
              <DialogClose asChild><Button type="button" variant="outline" disabled={remove.isPending}>Cancel</Button></DialogClose>
              <Button variant="destructive" disabled={remove.isPending} onClick={() => remove.mutate(deleteTarget.id)}>
                {remove.isPending ? 'Deleting…' : 'Delete'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
}

function ItemDialog({ existing, onClose }: { existing?: BillableItemRow; onClose: () => void }) {
  const qc = useQueryClient();
  const [code, setCode] = useState(existing?.code ?? '');
  const [name, setName] = useState(existing?.name ?? '');
  const [description, setDescription] = useState(existing?.description ?? '');
  const [unitKind, setUnitKind] = useState<UnitKind>(existing?.unit_kind ?? 'flat_monthly');
  const [priceCents, setPriceCents] = useState(existing?.default_unit_price_cents ?? 0);
  const [scope, setScope] = useState<BillableScope>(existing?.scope ?? 'reseller_to_customer');
  const [availableToReseller, setAvailableToReseller] = useState(existing?.available_to_reseller ?? true);
  const [active, setActive] = useState(existing?.active ?? true);
  const [error, setError] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: () => {
      const body: BillableItemBody = {
        code: code.trim(), name: name.trim(), description: description.trim() || null,
        unit_kind: unitKind, default_unit_price_cents: priceCents,
        scope, available_to_reseller: availableToReseller, active,
      };
      return existing ? customBilling.updateItem(existing.id, body) : customBilling.createItem(body);
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['billableItems'] }); onClose(); },
    onError: (err) => setError(err instanceof Error ? err.message : 'Save failed.'),
  });

  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{existing ? `Edit ${existing.name}` : 'New billable item'}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1">
              <Label htmlFor="bi-code">Code</Label>
              <Input id="bi-code" value={code} onChange={(e) => setCode(e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '_'))}
                disabled={!!existing} placeholder="e.g. storage_gb" />
              <p className="text-xs text-muted-foreground">Stable identifier — can&rsquo;t be changed later.</p>
            </div>
            <div className="space-y-1">
              <Label htmlFor="bi-name">Name</Label>
              <Input id="bi-name" value={name} onChange={(e) => setName(e.target.value)} />
            </div>
          </div>
          <div className="space-y-1">
            <Label htmlFor="bi-desc">Description</Label>
            <Input id="bi-desc" value={description} onChange={(e) => setDescription(e.target.value)} />
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1">
              <Label>Scope</Label>
              <Select value={scope} onValueChange={(v) => setScope(v as BillableScope)}>
                <SelectTrigger className="w-full"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="reseller_to_customer">Reseller → Customer</SelectItem>
                  <SelectItem value="owner_to_reseller">Owner → Reseller</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label>Unit kind</Label>
              <Select value={unitKind} onValueChange={(v) => setUnitKind(v as UnitKind)}>
                <SelectTrigger className="w-full"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="per_account">Per customer</SelectItem>
                  <SelectItem value="per_channel">Per channel</SelectItem>
                  <SelectItem value="per_gb">Per GB</SelectItem>
                  <SelectItem value="per_did">Per DID</SelectItem>
                  <SelectItem value="flat_monthly">Flat monthly</SelectItem>
                  <SelectItem value="per_event">Per event</SelectItem>
                  <SelectItem value="minimum_floor">Minimum floor</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="space-y-1 max-w-[240px]">
            <Label htmlFor="bi-price">Default unit price (cents)</Label>
            <Input id="bi-price" type="number" min={0} value={priceCents}
              onChange={(e) => setPriceCents(parseInt(e.target.value, 10) || 0)} />
            <p className="text-xs text-muted-foreground">
              ={(priceCents / 100).toLocaleString(undefined, { style: 'currency', currency: 'USD' })}
            </p>
          </div>
          <div className="flex flex-wrap gap-4">
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input type="checkbox" checked={availableToReseller}
                onChange={(e) => setAvailableToReseller(e.target.checked)}
                className="accent-primary" />
              Resellers can enable this for their customers
            </label>
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input type="checkbox" checked={active}
                onChange={(e) => setActive(e.target.checked)}
                className="accent-primary" />
              Active
            </label>
          </div>
          {error && <p role="alert" className="text-sm text-destructive">{error}</p>}
        </div>
        <DialogFooter>
          <DialogClose asChild><Button type="button" variant="outline" disabled={save.isPending}>Cancel</Button></DialogClose>
          <Button type="button" onClick={() => save.mutate()} disabled={save.isPending || !code.trim() || !name.trim()}>
            {save.isPending ? 'Saving…' : existing ? 'Save changes' : 'Create item'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
